import React, { useState } from 'react'
import {
  Badge,
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material'
import { Notifications as NotificationsIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '@hooks/useNotifications'
import NotificationList from './NotificationList'

const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const navigate = useNavigate()
  const { unreadCount, notifications, markAsRead, markAllAsRead, deleteNotification } = useNotifications()

  const open = Boolean(anchorEl)

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(open ? null : event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleViewAll = () => {
    handleClose()
    navigate('/app/settings/notifications')
  }

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box>
        <Tooltip title="Notifications">
          <IconButton
            color="inherit"
            onClick={handleToggle}
            aria-label={`${unreadCount} unread notifications`}
            sx={{
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-end"
          style={{ zIndex: 1400 }}
        >
          <Paper
            elevation={4}
            sx={{
              width: 360,
              maxHeight: 500,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              mt: 1,
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Notifications
                {unreadCount > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    ({unreadCount} unread)
                  </Typography>
                )}
              </Typography>
            </Box>

            <Box sx={{ overflow: 'auto', flex: 1 }}>
              <NotificationList
                notifications={notifications.slice(0, 10)}
                onMarkAsRead={(id) => markAsRead(id)}
                onMarkAllAsRead={() => markAllAsRead()}
                onDelete={(id) => deleteNotification(id)}
              />
            </Box>

            <Divider />
            <Box sx={{ px: 2, py: 1, textAlign: 'center' }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={handleViewAll}
              >
                View all notifications &amp; settings
              </Typography>
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}

export default NotificationBell
